<template>
  <div
    ref="triggerRef"
    class="relative inline-block text-left filter-dropdown mr-1 h-full"
    :data-filter-id="filter.id"
  >
    <a-tooltip
      :open="tooltipVisible"
      placement="bottomLeft"
      :overlay-style="{ maxWidth: '320px' }"
      :overlay-inner-style="{
        fontFamily: 'Proxima Nova, sans-serif',
        fontSize: '13px',
        lineHeight: '1.5',
        padding: '8px 12px',
      }"
      @openChange="handleTooltipOpenChange"
    >
      <template #title>
        <div>
          <div>{{ tooltipData.base }}</div>
          <template v-if="tooltipData.lines && tooltipData.lines.length > 0">
            <div style="margin-top: 6px">
              The following properties belong to this quick filter group:
            </div>
            <ul style="margin: 4px 0 0 16px; padding: 0; list-style-type: disc;">
              <li v-for="(line, idx) in tooltipData.lines" :key="idx">{{ line }}</li>
            </ul>
          </template>
        </div>
      </template>
      <div
        :class="[
          'flex',
          'items-center',
          'px-3',
          'py-[6px]',
          'rounded-lg',
          'filter-box',
          isFilterApplied && 'applied_filter',
        ]"
      >
        <div
          @click="toggleDropdown"
          type="button"
          :class="['flex', 'flex-row', 'items-center', 'flex-1', 'min-w-0', cursorClass]"
        >
          <div class="flex flex-row items-center gap-2 flex-1 min-w-0">
            <div class="flex proxima_nova items-center gap-1 min-w-0 flex-1">
              <span
                class="text_color text-[13px] font-semibold whitespace-nowrap"
                >{{ filter.name }}</span
              >
              <template v-if="isFilterApplied && selectedNames && selectedNames.length > 0">
                <span class="text-[13px] font-normal text_color">:</span>
                <span
                  class="text-[13px] font-semibold color_666 truncate"
                  :title="fullSelectedNames"
                  >{{ formattedSelectedNames }}</span
                >
              </template>
            </div>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW_GREY"
              alt="dropdown_icon"
              :class="['size-4', 'arrow-icon', isOpen ? 'arrow-rotated' : '']"
            />
          </div>
        </div>
      </div>
    </a-tooltip>

    <Teleport to="body">
      <transition name="dropdown-fade" mode="out-in">
        <div
          ref="dropdownRef"
          v-if="isOpen"
          :class="[
            'origin-top-left fixed dropdown_list rounded-lg mt-2 py-2 z-[9999]',
            filter.customWidth ? filter.customWidth : 'w-56',
          ]"
          :style="dropdownStyle"
          :data-filter-dropdown-id="filter.id"
          @mousedown.stop
          @click.stop
        >
          <slot></slot>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { formatSelectedNames } from "@/src/utils/utils.js";

const props = defineProps({
  filter: { type: Object, required: true },
  isOpen: { type: Boolean, required: true },
  selectedCount: { type: Number, required: true },
  isFilterApplied: { type: Boolean, required: true },
  selectedNames: { type: Array, default: () => [] },
  tooltipData: { type: Object, default: () => ({ base: "", lines: [] }) },
  title: { type: String, default: "" },
  ondelete: { type: Function, default: null },
  hideTitle: { type: Boolean, default: false },
  locked: { type: Boolean, default: false },
});

const emit = defineEmits(["toggle", "clear"]);

const triggerRef = ref(null);
const dropdownPosition = ref({ top: 0, left: 0 });

const formattedSelectedNames = computed(() => {
  return formatSelectedNames(props.selectedNames);
});

const fullSelectedNames = computed(() => {
  if (!props.selectedNames || props.selectedNames.length === 0) return "";
  return props.selectedNames.join(", ");
});

const cursorClass = computed(() => {
  return props.locked ? 'cursor-default' : 'cursor-pointer';
});

const dropdownStyle = computed(() => {
  return {
    top: `${dropdownPosition.value.top}px`,
    left: `${dropdownPosition.value.left}px`,
  };
});

const updateDropdownPosition = () => {
  if (!triggerRef.value || !props.isOpen) return;

  nextTick(() => {
    const rect = triggerRef.value.getBoundingClientRect();
    dropdownPosition.value = {
      top: rect.bottom + 8,
      left: rect.left,
    };
  });
};

const tooltipVisible = ref(false);

const hasTooltipContent = computed(() => {
  return !!(props.tooltipData?.base || props.tooltipData?.lines?.length > 0);
});

const handleTooltipOpenChange = (visible) => {
  if (!props.isOpen && hasTooltipContent.value) {
    tooltipVisible.value = visible;
  }
};

watch(() => props.isOpen, (open) => {
  if (open) {
    tooltipVisible.value = false;
    updateDropdownPosition();
  }
});

const toggleDropdown = () => {
  if (props.locked) return;
  tooltipVisible.value = false;
  emit("toggle", props.filter.id);
};

onMounted(() => {
  window.addEventListener('scroll', updateDropdownPosition, true);
  window.addEventListener('resize', updateDropdownPosition);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateDropdownPosition, true);
  window.removeEventListener('resize', updateDropdownPosition);
});
</script>

<style scoped>
.filter-box {
  background-color: var(--white-opacity-4);
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
  box-sizing: border-box;
}

.filter-box:hover {
  background-color: var(--white-opacity-8);
}

.applied_filter {
  animation: highlight 0.3s ease-in-out;
  background-color: var(--white-opacity-4);
  border: 1px solid var(--white-opacity-8);
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
}

.dropdown_list {
  background: var(--dropdown-bg);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.arrow-icon {
  transition: transform 0.3s ease;
}

.arrow-rotated {
  transform: rotate(180deg);
}

@keyframes highlight {
  0% {
    background-color: var(--white-opacity-12);
  }
  50% {
    background-color: var(--white-opacity-16, rgba(255, 255, 255, 0.16));
  }
  100% {
    background-color: var(--white-opacity-12);
  }
}

.dropdown-fade-enter-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1);
  transform-origin: top center;
}

.dropdown-fade-leave-active {
  transition:
    opacity 0.2s cubic-bezier(0.25, 0.1, 0.25, 1),
    transform 0.2s cubic-bezier(0.25, 0.1, 0.25, 1);
  transform-origin: top center;
  pointer-events: none;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.dropdown-fade-enter-to,
.dropdown-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
